import { objectUtils } from '@festina-life/core'
import cx from 'classnames'
import type { ComponentProps, ReactNode } from 'react'
import { useState } from 'react'
import { ComboBox, Input, ListBox, Popover } from 'react-aria-components'

import { IconButton } from '../../actions'
import { CircularSpinner } from '../../feedback'
import { useTranslate } from '../../hooks'
import { dropdownStyles } from '../../libs/dropdownStyles'
import { rawTextInputStyles } from '../textInput/rawTextInputStyles'
import { filterItemsByText, findOrCreateValue } from './RawSelect.utils'
import { RawSelectOption } from './RawSelectOption'
import { selectStyles } from './selectStyles'
import { useManageUserInput } from './useManageUserInput'
import { type SelectableItem, useSelectableItems } from './useSelectableItems'

/**
 * A custom select component.
 * Use when presenting the user for a selection between 3 and 10 ish
 * For fewer element use radio buttons
 * For more use combobox
 */
type Props<T> = Pick<
  ComponentProps<'input'>,
  'id' | 'name' | 'onBlur' | 'onClick'
> & {
  /**
   * The options presented in the select. If the presented text differs from the actual value, use a list of Option objects otherwise just use strings
   */
  options: readonly T[]
  /**
   * Classname applied to the container element
   */
  className?: string
  /**
   * Classname applied to each option in the select. Use to change e.g. break behaviour
   */
  optionClassName?: string
  /**
   * Classname applied to the dropdown in the select. Use to change e.g. width
   */
  optionsClassName?: string
  /**
   * Provides default styling to the input element. Ensuring that there is a consistent look and feel across the application.
   */
  isAddOn?: boolean
  /**
   * Displays the component in an error state
   */
  isInError?: boolean
  /**
   * Placeholder text shown when no value is selected
   */
  placeholder?: string
  /**
   * Whether to show a button for clearing the selected value. If true, a button will be shown that when clicked will clear the selected value and set the input value to an empty string.
   * @defaultValue `true`
   */
  shouldShowClearButton?: boolean
  /**
   * Disables the Select
   */
  disabled?: boolean
  /**
   * callback to handle value changes
   */
  onChange: (value: T | undefined) => void
  /**
   * A callback to pass the current value up when onBlur is called.
   * This is needed since the FocusEvent from <div> where the onBlur is called does not have the value.
   */
  onBlurValue?: (value: T | undefined) => void
  /**
   * The default selected value
   */
  value?: T
  /**
   * Function for rendering the contents of an item in the list
   */
  renderFn?: (item: T) => string | ReactNode
  /**
   * Function for rendering the current value. May be used to render a shortened version of the value to avoid the component dimensions
   * from growing when the dropdown is closed.
   */
  renderValueFn?: (item: T) => string | ReactNode
  /**
   * Function for retrieving the key for an item in the list
   */
  keyFn?: (item: T) => string
  /**
   * Skips DOM-based option text resolution. Use when the rendered options do
   * not need asynchronous text extraction for filtering or accessibility.
   * @defaultValue `false`
   */
  skipOptionResolution?: boolean
  /**
   * Indicates whether the select is currently loading options.
   */
  isLoading?: boolean
  /**
   * Called with the input text whenever the user types in the input.
   */
  onInputChange?: (inputText: string) => void
  /**
   * When there are no options available and the user has not searched. Shows default text telling the user to write something.
   */
  shouldShowAsyncSearchPromptWhenEmpty?: boolean
  /**
   * Size of the input field. This is used to set the width of the input field and should be set according to the expected length of the selected value. If not set, the input field will have a default width that may not be suitable for all use cases.
   */
  inputSize?: number
  /**
   * When we using async API requests one may not want to have both filtering enabled.
   */
  shouldSkipLocalFiltering?: boolean
  sortFn?: (
    left: { value: T; textValue: string },
    right: { value: T; textValue: string },
  ) => number
} & (
    | {
        /**
         * Creates a generic type T from an input string. This is the reverse of the renderFn and should only be provided
         * when isValueCreator is true.
         */
        valueFromString: (value: string) => T
        isValueCreator: true
      }
    | {
        valueFromString?: never
        isValueCreator?: never
      }
  )

export const RawSelect = <T,>({
  options,
  // By default this is set to true to easier accommodate non required fields
  shouldShowClearButton = true,
  placeholder,
  className,
  isAddOn = false,
  optionClassName,
  optionsClassName,
  isInError = false,
  disabled = false,
  onBlurValue,
  onChange,
  value,
  renderFn = (entry: T) => `${entry}`,
  renderValueFn,
  keyFn = (entry: T) => objectUtils.objectToSortedString(entry),
  id,
  name, // we deliberately do not pass the name as part of rest since we use id to link label and input for testing
  onBlur,
  onClick,
  inputSize,
  isValueCreator,
  valueFromString = (value) => value as unknown as T,
  sortFn,
  skipOptionResolution = false,
  onInputChange,
  isLoading,
  shouldShowAsyncSearchPromptWhenEmpty,
  shouldSkipLocalFiltering = false,
  ...rest
}: Props<T>) => {
  const translate = useTranslate()
  const { items, extractionMirrors } = useSelectableItems({
    options,
    renderFn,
    renderValueFn,
    keyFn,
    skipOptionResolution: skipOptionResolution || !renderFn,
  })

  const hasValue = value !== undefined
  const valueKey = hasValue ? keyFn(value) : undefined
  const valueItem = items.find((item) => item.key === valueKey)

  const getCurrentValueText = (currentValue: T | undefined) =>
    (currentValue === undefined
      ? ''
      : (renderValueFn?.(currentValue) ?? renderFn(currentValue) ?? '')
    ).toString()

  const syncedInputValue =
    valueItem?.textValue ??
    (isValueCreator && hasValue ? getCurrentValueText(value) : '')

  const [isFocused, setIsFocused] = useState(false)
  const selectInputText = useManageUserInput({
    renderedValueFromItem: valueItem?.renderedInputValue,
    inputValueAsText: syncedInputValue,
    valueKey,
    isFocused,
  })
  const shouldShowClearAction = hasValue && !disabled && shouldShowClearButton

  // Filter uses resolved plain text to support both string and ReactNode labels.
  const visibleOptions =
    selectInputText.isFiltering && !shouldSkipLocalFiltering
      ? filterItemsByText(items, selectInputText.inputValue)
      : items

  const selectItem = (nextSelectedItem: SelectableItem<T> | undefined) => {
    const nextValue = nextSelectedItem?.value
    const nextInputValue = nextSelectedItem?.textValue ?? ''

    selectInputText.commit(nextInputValue)
    onChange(nextValue)
    onBlurValue?.(nextValue)
  }

  const handleCustomValue = (userInput: string) => {
    if (!isValueCreator) {
      return
    }

    const committedValue = findOrCreateValue({
      userInput,
      items,
      valueFromString,
    })

    if (!committedValue) {
      return
    }

    selectInputText.commit(committedValue.text)
    onChange(committedValue.value)
    onBlurValue?.(committedValue.value)
  }

  return (
    <>
      <ComboBox
        aria-label="Select"
        isDisabled={disabled}
        onBlur={onBlur}
        menuTrigger="focus"
        items={visibleOptions.sort(sortFn)}
        allowsCustomValue={!!isValueCreator}
        // When doing asynchronous search, allow empty collection is necessary else the dropdown query will not appear.
        allowsEmptyCollection={!!onInputChange}
        inputValue={selectInputText.inputValue}
        value={
          visibleOptions.find((x) => x.key === valueItem?.key)?.key ?? null
        }
        onInputChange={selectInputText.update}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' || !isValueCreator) {
            return
          }

          handleCustomValue(selectInputText.inputText)
          event.preventDefault()
        }}
        className={cx('relative w-full', className)}
        onChange={(key) => {
          const nextSelectedItem = visibleOptions.find(
            (item) => item.key === key,
          )

          if (nextSelectedItem) {
            selectItem(nextSelectedItem)
          } else if (isValueCreator && typeof key === 'string') {
            handleCustomValue(key)
          }
        }}
        {...rest}
      >
        <Input
          id={id}
          name={name}
          disabled={disabled}
          className={cx(
            rawTextInputStyles({
              isInError,
              disabled,
            }),
            inputSize && 'w-auto!',
            'break-all',
            shouldShowClearAction ? 'pe-10' : 'pe-5',
            isAddOn ? 'rounded-e-none!' : '',
          )}
          size={inputSize}
          onChange={(event) => onInputChange?.(event.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={(event) => {
            setIsFocused(false)

            // On normal blur in creator mode, commit any free-text value
            if (selectInputText.isFiltering) {
              if (isValueCreator) {
                handleCustomValue(selectInputText.inputText)
              } else {
                selectInputText.clear()
              }
            }

            onBlur?.(event)
          }}
          onClick={onClick}
          placeholder={
            selectInputText.shouldShowInputValueOverlay || isFocused
              ? undefined
              : placeholder
          }
        />

        {selectInputText.shouldShowInputValueOverlay && valueItem && (
          <div
            className={cx(
              'pointer-events-none absolute inset-y-0 inset-s-2 flex items-center truncate',
              shouldShowClearAction ? 'inset-e-14' : 'inset-e-8',
            )}
          >
            {/*
            Visual overlay for non-string selected content while input value stays
            plain-text for keyboard handling and filtering.
          */}
            {valueItem.renderedInputValue}
          </div>
        )}
        <div className={cx(selectStyles.inputIconsWrapper, 'inset-e-0!')}>
          {isLoading && <CircularSpinner className="size-4 p-2" />}
          {shouldShowClearAction && !isLoading && (
            <IconButton
              iconName="XMark"
              className="text-current!"
              disabled={disabled}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                // Clear both visual and committed selection state.
                selectInputText.clear()
                onChange(undefined)
              }}
            />
          )}
          <IconButton iconName="ChevronUpDown" className="text-current!" />
        </div>
        <Popover
          offset={4}
          className={cx(
            selectStyles.options,
            'min-w-(--trigger-width) max-w-[min(calc(var(--trigger-width)*3),90vw)]!',
            optionsClassName,
            dropdownStyles,
          )}
        >
          {items.length === 0 && (
            <>
              {shouldShowAsyncSearchPromptWhenEmpty &&
                !selectInputText.isFiltering && (
                  <p className="px-2 pt-1">
                    {translate('select.pleaseTypeToSearch')}
                  </p>
                )}
              {selectInputText.isFiltering && !isLoading && (
                <p className="px-2 pt-1">{translate('select.noResults')}</p>
              )}
            </>
          )}
          <ListBox className="outline-hidden">
            {(currentItem: SelectableItem<T>) => (
              <RawSelectOption
                currentItem={currentItem}
                disabled={disabled}
                optionClassName={optionClassName}
              />
            )}
          </ListBox>
        </Popover>
      </ComboBox>
      {extractionMirrors}
    </>
  )
}
